import type { GameOptions } from '@durak/shared';

interface Props {
  value: GameOptions;
  onChange: (partial: Partial<GameOptions>) => void;
  /** Hide options that only apply when creating a brand-new room (e.g. isPrivate). */
  hidePrivate?: boolean;
  /** Hide bot-related options (e.g. when the room is already started with humans). */
  hideBotDifficulty?: boolean;
  /** Limit maxPlayers to at least this many — used by lobby so host can't shrink below current player count. */
  minMaxPlayers?: number;
}

export function GameOptionsPanel({ value: opts, onChange, hidePrivate, hideBotDifficulty, minMaxPlayers = 2 }: Props) {
  return (
    <div className="rounded-xl border border-ink-700/60 p-4 space-y-3">
      <OptRow label="Deck size">
        <select
          className="input !py-1.5"
          value={opts.deckSize}
          onChange={(e) => onChange({ deckSize: Number(e.target.value) as 36 | 52 })}
        >
          <option value={36}>36 cards (classic)</option>
          <option value={52}>52 cards</option>
        </select>
      </OptRow>
      <OptRow label="Max players">
        <input
          type="number"
          min={minMaxPlayers}
          max={6}
          className="input !py-1.5 w-20"
          value={opts.maxPlayers}
          onChange={(e) => onChange({ maxPlayers: Math.max(minMaxPlayers, Math.min(6, Number(e.target.value) || minMaxPlayers)) })}
        />
      </OptRow>
      <OptRow label="Throw-in by all players">
        <Toggle value={opts.throwInByAll} onChange={(v) => onChange({ throwInByAll: v })} />
      </OptRow>
      <OptRow label="Transferable defense (Perevodnoy)">
        <Toggle value={opts.transferable} onChange={(v) => onChange({ transferable: v })} />
      </OptRow>
      <OptRow label="Refill hands from deck">
        <Toggle value={opts.refillHands} onChange={(v) => onChange({ refillHands: v })} />
      </OptRow>
      <OptRow label="Max attack cards per round">
        <input
          type="number"
          min={1}
          max={12}
          className="input !py-1.5 w-20"
          value={opts.maxAttackCards}
          onChange={(e) => onChange({ maxAttackCards: Math.max(1, Math.min(12, Number(e.target.value) || 6)) })}
        />
      </OptRow>
      <OptRow label="Rotate seats around the table">
        <Toggle value={opts.rotateSeats} onChange={(v) => onChange({ rotateSeats: v })} />
      </OptRow>
      {!hidePrivate && (
        <>
          <OptRow label="Private room (invite only)">
            <Toggle value={opts.isPrivate} onChange={(v) => onChange({ isPrivate: v })} />
          </OptRow>
          {opts.isPrivate && (
            <p className="text-xs text-ink-300 -mt-1">
              Hidden from the public lobby. Friends can join with the room code or invite link.
            </p>
          )}
        </>
      )}
      {!hideBotDifficulty && (
        <>
          <OptRow label="Bot difficulty">
            <select
              className="input !py-1.5"
              value={opts.botDifficulty}
              onChange={(e) => onChange({ botDifficulty: e.target.value as any })}
            >
              <option value="easy">Easy</option>
              <option value="medium">Medium</option>
              <option value="hard">Hard</option>
            </select>
          </OptRow>
          <p className="text-xs text-ink-300 -mt-1">
            Bot games don't affect your stats or leaderboard.
          </p>
        </>
      )}
    </div>
  );
}

function OptRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-sm">{label}</span>
      {children}
    </div>
  );
}

function Toggle({ value, onChange }: { value: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!value)}
      className={`w-11 h-6 rounded-full transition relative ${value ? 'bg-felt-500' : 'bg-ink-600'}`}
      aria-pressed={value}
    >
      <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${value ? 'translate-x-5' : ''}`} />
    </button>
  );
}
